// composables/useAuth.client.ts
import { useAuthStore } from '~/store/auth'
import { trackLoginSuccess } from '~/tracking/loginEvents.js'
import { useEncryptEmail } from '~/composables/useEncryptEmail'

interface GoogleTokenResponse {
	access_token: string
	error?: string
}

interface GoogleOAuth2 {
	initTokenClient: (config: {
		client_id: string
		scope: string
		callback: (response: GoogleTokenResponse) => void | Promise<void>
	}) => {
		requestAccessToken: (options: { prompt: string }) => void
	}
}

declare global {
	interface Window {
		google?: {
			accounts?: {
				oauth2?: GoogleOAuth2
			}
		}
	}
}

const MAX_RETRIES = 20 // 2 segundos máximo (20 × 100ms)

const handleOnSuccess = async (accessToken: string) => {
	const authStore = useAuthStore()
	const guestScanData = useGuestScanData()
	const encrypt = await useEncryptEmail()
	const result = await authStore.saveAuthGoogle({ token: accessToken })
	if (!result.success) return

	if (guestScanData.hasPendingScanData()) {
		guestScanData.mergePendingScanMeta({ authMethod: 'google' })
	}

	const { email, id } = result.result.user
	const emailUser = await encrypt.getEncryptEmail({ text: email })
	trackLoginSuccess({ method: 'google', email: emailUser, user_id: id })
	await authStore.fetchMe()

	authStore.SET_STATE_AUTH({ type: 'showModalLogin', value: false })
	authStore.SET_STATE_AUTH({ type: 'showModalSignUp', value: false })
}

/**
 * Client-only composable that initialises the Google OAuth2 token client and exposes a
 * `login()` function to trigger the Google account picker.
 * Returns `{ isReady, login }` — `isReady` becomes `true` once the GSI library is loaded.
 * On the server, returns safe no-op stubs so SSR does not crash.
 */
export const useAuth = () => {
	if (import.meta.server) {
		return { isReady: ref(false), login: (() => {}) as any }
	}

	const config = useRuntimeConfig()
	const isReady = ref(false)
	// ← tokenClient ahora es local al composable, no global
	let tokenClient: any = null

	onMounted(() => {
		let retries = 0

		const tryInit = () => {
			if (retries >= MAX_RETRIES) {
				console.warn('Google GSI no disponible (¿adblocker?)')
				return
			}

			if (window.google?.accounts?.oauth2) {
				tokenClient = window.google.accounts.oauth2.initTokenClient({
					client_id: config.public.googleSignIn.clientId,
					scope: 'openid email profile',
					callback: async (response: GoogleTokenResponse) => {
						if (response?.error) {
							console.error('Google auth error:', response.error)
							return
						}
						await handleOnSuccess(response.access_token)
					},
				})
				isReady.value = true
			} else {
				retries++
				setTimeout(tryInit, 100)
			}
		}

		tryInit()
	})

	const login = () => {
		if (!isReady.value || !tokenClient) return
		tokenClient.requestAccessToken({ prompt: 'select_account' })
	}

	return { isReady: readonly(isReady), login }
}
